import { useQuery } from '@tanstack/react-query';
import { useMemo } from 'react';
import { gqlClient } from '../clients';
import { graphql } from '../gql/gql';
import type { GlobalUser, MyAuthDetailsQuery } from '../gql/graphql';
import { consumePrefetch } from '../prefetch/consume-prefetch';
import { MY_AUTH_DETAILS_OPERATION } from '../prefetch/operations';
import { queryClient } from '../query-client';
import { useAuth } from '../stores/auth';

const myAuthDetailsDocument = graphql(/* GraphQL */ `
  query myAuthDetails {
    myAuthDetails {
      id
      email
      name
      defaultProfileId
      activeProfileId
      isEmulatingAsTyroUser
      profiles {
        id
        nickName
        avatarUrl
        tenant {
          tenant
          name
          imgUrl
          tenantType
        }
        profileType {
          name
          description
          userType
        }
        permissionIds
        partyId
        tenantId
        blocked
      }
    }
  }
`);

export const userKeys = {
  all: ['user'] as const,
  details: () => [...userKeys.all, 'details'] as const,
};

const userQuery = {
  queryKey: userKeys.details(),
  queryFn: async () => {
    const prefetched = await consumePrefetch<MyAuthDetailsQuery>(
      MY_AUTH_DETAILS_OPERATION,
    );
    return prefetched ?? gqlClient.request(myAuthDetailsDocument);
  },
  staleTime: 1000 * 60 * 10,
};

export async function getUser() {
  const { myAuthDetails: user } = await queryClient.fetchQuery(userQuery);

  return {
    user,
    activeProfile: findActiveProfile(user),
  };
}

export function findActiveProfile(user: MyAuthDetailsQuery['myAuthDetails']) {
  return user?.profiles?.find(
    (profile) => profile?.id === user.activeProfileId,
  );
}

export function useUser() {
  const { isTokenInitialized, isAuthenticated } = useAuth();

  const {
    data: user,
    isLoading,
    ...queryProps
  } = useQuery({
    ...userQuery,
    enabled: isTokenInitialized,
    select: ({ myAuthDetails }) => ({
      ...myAuthDetails,
      profiles: myAuthDetails?.profiles?.map((profile) => ({
        ...profile,
        nickName: profile?.nickName ?? myAuthDetails?.name ?? null,
      })),
    }),
  });

  return useMemo(
    () => ({
      user,
      activeProfile: findActiveProfile(user as GlobalUser),
      isLoading,
      isInitialized: isTokenInitialized && Boolean(user),
      isAuthenticated,
      ...queryProps,
    }),
    [user, isLoading, isTokenInitialized, isAuthenticated, queryProps],
  );
}

export type ReturnTypeFromGetUser = NonNullable<
  Awaited<ReturnType<typeof getUser>>
>;
export type ReturnTypeFromUseUser = NonNullable<ReturnType<typeof useUser>>;
